import { useEffect, useState } from "react";
import { Modal, Tab } from "react-bootstrap";
import { ProjectDetails } from "./ProjectDetails";
import { CreateProject } from "./CreateProject";
import useLocalStorageState from "use-local-storage-state";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import { useGetRequest } from "../../Utils/hooks/QueryHandler";
import DeleteModal from "../DeleteModal";
import { toast } from "sonner";
import { DeleteRequest, GetRequest, PutRequest } from "../../Utils/hooks/ApiHandler";
import ProjectSkeletonLoader from "./ProjectSkeletonLoader";
import { UpgradePlan } from "../../CommonComponents/UpgradePlan";
import { AuthorizationCard } from "../../CommonComponents/AuthorizationCard";
import { ProjectHeader } from "./ProjectModule/Projectheader";
import { ProjectListing } from "./ProjectModule/Projectlisting";

const Projects = () => {
  const { headers, extras, customerId, loginUserId } = useGetEssentials();

  // rbac permission object
  const [PERMISSION, _] = useLocalStorageState("permission");

  // project permission object
  const projectGrant = PERMISSION?.project;
  const isProject = projectGrant?.isAuthorized;

  const [show, setShow] = useState(false);
  const [showProjectCanvas, setProjectOffcanvas] = useState(false);
  const [project_id, setProject_id] = useState();
  const [editedMode, setEditMode] = useState(false);
  const [projectListData, setProjectListData] = useState([]);
  const [projectIdForDetailPage, setProjectIdForDetailPage] = useState(null);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardData, setCardData] = useState([]);
  // delete modal loading state
  const [loading, setLoading] = useState(false);
  // States for using pagination
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [projectStatus, setProjectStatus] = useState(null);

  const params = {
    limit,
    offset,
    search,
  };

  // columns shown in the listing table - all visible by default
  const columnOptions = [
    { key: "contact", label: "Contact", mandatory: true },
    { key: "project", label: "Project" },
    { key: "description", label: "Description" },
    { key: "teams", label: "Teams" },
    { key: "start_date", label: "Start Date" },
    { key: "end_date", label: "End Date" },
  ];
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem("visibleProjectColumns");
    const allColumnKeys = columnOptions?.map((col) => col.label);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing visible columns:", e);
      }
    }
    return allColumnKeys;
  });

  const {
    data: projectData,
    error,
    refetch: fetchProjectList,
    isLoading,
    isError,
  } = useGetRequest(
    `project/fetchProjects/${customerId}/${loginUserId}`,
    "getProject",
    headers,
    extras,
    params,
  );

  useEffect(() => {
    fetchProjectList();
    setProjectListData([]);

    if (projectData?.status === "success") {
      setProjectListData(projectData.data);
    } else if (projectData?.status === "fail") {
      setProjectListData([]);
    } else if (isError) {
      console.error("Error fetching quotation:", error);
    }
  }, [projectData, isError, error]);

  // Handle page change from react-paginate
  const handlePageChange = (selectedItem) => {
    setOffset(selectedItem.selected);
  };

  const handleCloseModal = () => {
    setShowConfirm(false);
    setLoading(false);
    setProjectStatus(null);
    setProject_id(null);
  };
  const handleMarkedAsComplete = (project) => {
    setShowConfirm(true);
    setProjectStatus(project?.project_status);
    setProject_id(project?.id);
  };
  const handleConfirm = async () => {
    try {
      const payload = {
        project_id: project_id,
        project_status:
          projectStatus === "incomplete" ? "complete" : "incomplete",
      };
      const res = await PutRequest(
        `project/markComplete`,
        payload,
        { headers },
        extras,
      );

      if (!res || res.status === "fail") {
        return toast.error(res?.message || "something went wrong!");
      }
      toast.success(res.message);
      fetchCardData();
      fetchProjectList();
    } catch (error) {
      console.error(error);
    } finally {
      handleCloseModal();
    }
  };

  const handleProjectEditClicked = (project) => {
    setProject_id(project?.id);
    setEditMode(true);
    setProjectOffcanvas(true);
  };

  const handleDetailPageClicked = (project) => {
    setProjectIdForDetailPage(null);
    setProjectIdForDetailPage(project?.id);
    setShow(true);
  };

  const handleOnCloseClick = () => {
    setProjectIdForDetailPage(null);
    setShow(false);
  };

  // Trigger delete modal
  const onDelete = (id) => {
    setDepartmentToDelete(id);
    setShowDeleteModal(true);
  };
  // Confirm delete and call API
  const handleConfirmedDelete = async () => {
    setLoading(true);
    try {
      const item = projectListData.find((d) => d.id === departmentToDelete);
      if (!item) return;

      const res = await DeleteRequest(
        `project/deleteProject/${departmentToDelete}/${loginUserId}`,
        { headers },
        extras,
      );
      if (res?.status === "success") {
        toast.success(res.message);
        await fetchProjectList();
      } else if (res.status === "fail") {
        toast.error(res.message);
      }
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDepartmentToDelete(null);
    }
  };

  const fetchCardData = async () => {
    try {
      const res = await GetRequest(
        `/project/projectCards/${customerId}/${loginUserId}`,
        { headers },
        extras,
      );

      if (res.status === "fail" || res.status !== "success") {
        return;
      }

      setCardData(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  if (!isProject) {
    return <UpgradePlan moduleName="Projects" />;
  }

  if (!projectGrant?.isView) {
    return (
      <div style={{ position: "absolute", left: "40%", top: "10rem" }}>
        <AuthorizationCard />
      </div>
    );
  }

  return (
    <>
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmedDelete}
        isLoading={loading}
        message="Are you sure you want to delete this item?"
      />
      <Tab.Container
        defaultActiveKey="tab-sales"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 200px)",
          overflow: "hidden",
        }}
      >
        <Tab.Content
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Tab.Pane
            className="p-0 pt-1 pb-3"
            eventKey="tab-sales"
            style={{
              border: "none",
              animation: "cardEntry 1s ease-out forwards",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <ProjectHeader
              columns={columns}
              setColumns={setColumns}
              columnOptions={columnOptions}
              setOffset={setOffset}
              setSearch={setSearch}
              setProjectOffcanvas={setProjectOffcanvas}
            />

            {isLoading ? (
              <ProjectSkeletonLoader />
            ) : (
              <ProjectListing
                projectListData={projectListData}
                cardData={cardData}
                offset={offset}
                setOffset={setOffset}
                limit={limit}
                setLimit={setLimit}
                totalItems={projectData?.total_count || 0}
                isLoading={isLoading}
                columns={columns}
                handlePageChange={handlePageChange}
                handleDetailPageClicked={handleDetailPageClicked}
                handleProjectEditClicked={handleProjectEditClicked}
                handleMarkedAsComplete={handleMarkedAsComplete}
                onDelete={onDelete}
                setProjectOffcanvas={setProjectOffcanvas}
              />
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {show && (
        <ProjectDetails
          show={show}
          onClose={() => handleOnCloseClick()}
          projectIdForDetailPage={projectIdForDetailPage}
          setProjectIdForDetailPage={setProjectIdForDetailPage}
          fetchProjectList={fetchProjectList}
        />
      )}

      {showProjectCanvas && (
        <CreateProject
          show={showProjectCanvas}
          setProjectOffcanvas={setProjectOffcanvas}
          editedMode={editedMode}
          setEditMode={setEditMode}
          project_id={project_id}
          setProject_id={setProject_id}
          fetchProjectList={fetchProjectList}
        />
      )}
      {showConfirm && (
        <Modal show={showConfirm} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Mark Project As Completed</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to mark as complete this Project?</p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-default-outline"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button className="btn btn-default" onClick={handleConfirm}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default Projects;